// src/templateManager/components/tabs/RiskAreasTab.jsx
import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import {
  Stack,
  Card,
  Text,
  Button,
  Group,
  Accordion,
  Badge,
  Alert,
  TextInput,
  Textarea
} from '@mantine/core';
import { IconPlus, IconInfoCircle, IconTrash } from '@tabler/icons-react';
import RiskAreaBuilder from '../builders/RiskAreaBuilder';

/**
 * RiskAreasTab - Configure risk areas and sources
 * Manages the risk taxonomy: areas → sources
 */
function RiskAreasTab() {
  const { control, watch, setValue } = useFormContext();

  const riskAreas = watch('taxonomy.riskAreas') || {};
  const areaKeys = Object.keys(riskAreas);

  // Add new risk area
  const addRiskArea = () => {
    const newAreaKey = `customArea${Date.now()}`;
    const newArea = {
      label: 'New Risk Area',
      description: '',
      riskSources: []
    };

    setValue(`taxonomy.riskAreas.${newAreaKey}`, newArea, { shouldDirty: true });
  };

  // Remove risk area
  const removeRiskArea = (areaKey) => {
    const updated = { ...riskAreas };
    delete updated[areaKey];
    setValue('taxonomy.riskAreas', updated, { shouldDirty: true });
  };

  // Get icon for risk area
  const getAreaIcon = (areaKey) => {
    const icons = {
      wellbeing: '🏥',
      humanRights: '👤',
      lawAndOrder: '⚖️',
      societalImpact: '🌍',
      essentialServices: '💰',
      safetyApplications: '🚨',
      publicServiceAccess: '🏛️'
    };
    return icons[areaKey] || '📋';
  };

  // Get display name for area key
  const getAreaDisplayName = (areaKey) => {
    const area = riskAreas[areaKey];
    return area?.label || areaKey;
  };

  return (
    <Stack gap="lg">
      {/* Info */}
      <Alert icon={<IconInfoCircle size={16} />} color="blue">
        Risk areas define the taxonomy of risks that will be assessed. Each area contains risk sources that users can select during assessment.
      </Alert>

      {/* Risk Areas */}
      {areaKeys.length === 0 ? (
        <Card withBorder p="xl">
          <Stack align="center" gap="md">
            <Text size="lg" c="dimmed">No risk areas configured</Text>
            <Text size="sm" c="dimmed">Add your first risk area to get started</Text>
            <Button leftSection={<IconPlus size={16} />} onClick={addRiskArea}>
              Add Risk Area
            </Button>
          </Stack>
        </Card>
      ) : (
        <Accordion multiple defaultValue={areaKeys}>
          {areaKeys.map((areaKey) => (
            <Accordion.Item key={areaKey} value={areaKey}>
              <Accordion.Control>
                <Group>
                  <Text size="lg">{getAreaIcon(areaKey)}</Text>
                  <div style={{ flex: 1 }}>
                    <Group gap="xs">
                      <Text fw={600}>{getAreaDisplayName(areaKey)}</Text>
                      <Badge size="sm" variant="light">
                        {riskAreas[areaKey]?.riskSources?.length || 0} sources
                      </Badge>
                    </Group>
                    <Text size="sm" c="dimmed">
                      {riskAreas[areaKey]?.description || 'No description'}
                    </Text>
                  </div>
                </Group>
              </Accordion.Control>

              <Accordion.Panel>
                <Stack gap="md" mt="md">
                  {/* Area Metadata */}
                  <Card withBorder p="md" bg="gray.0">
                    <Stack gap="sm">
                      <Text size="sm" fw={600}>Area Configuration</Text>

                      <Controller
                        name={`taxonomy.riskAreas.${areaKey}.label`}
                        control={control}
                        render={({ field }) => (
                          <TextInput
                            {...field}
                            label="Area Label"
                            placeholder="e.g., Well-being"
                            required
                          />
                        )}
                      />

                      <Controller
                        name={`taxonomy.riskAreas.${areaKey}.description`}
                        control={control}
                        render={({ field }) => (
                          <Textarea
                            {...field}
                            label="Description"
                            placeholder="Describe this risk area..."
                            minRows={2}
                          />
                        )}
                      />

                      <Group justify="flex-end">
                        <Button
                          variant="subtle"
                          color="red"
                          size="xs"
                          leftSection={<IconTrash size={14} />}
                          onClick={() => removeRiskArea(areaKey)}
                        >
                          Delete Area
                        </Button>
                      </Group>
                    </Stack>
                  </Card>

                  {/* Risk Sources */}
                  <RiskAreaBuilder areaKey={areaKey} />
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          ))}
        </Accordion>
      )}

      {/* Add Area Button */}
      {areaKeys.length > 0 && (
        <Button
          variant="light"
          leftSection={<IconPlus size={16} />}
          onClick={addRiskArea}
          fullWidth
        >
          Add Risk Area
        </Button>
      )}

      {/* Summary */}
      <Card withBorder p="md" bg="gray.0">
        <Stack gap="xs">
          <Text size="sm" fw={600}>Summary</Text>
          <Group gap="md">
            <Badge variant="dot">Total Areas: {areaKeys.length}</Badge>
            <Badge variant="dot" color="blue">
              Total Sources: {areaKeys.reduce((sum, key) => sum + (riskAreas[key]?.riskSources?.length || 0), 0)}
            </Badge>
          </Group>
        </Stack>
      </Card>
    </Stack>
  );
}

export default RiskAreasTab;
